import createDOMPurify from 'dompurify';

/**
 * Question types whose answer is unconstrained text typed by the respondent.
 * Anything in this list must be sanitized before it is stored, because survey
 * answers (e.g. the customer's name) are later interpolated into HTML on the
 * public share result page.
 */
const FREE_INPUT_QUESTION_TYPES: ReadonlySet<string> = new Set([
    'text',
    'textarea',
    'email',
    'number',
    'tel',
    'phone',
]);

export function isFreeInputQuestionType(
    questionType: string | undefined | null,
): boolean {
    if (!questionType) {
        return false;
    }

    return FREE_INPUT_QUESTION_TYPES.has(questionType.toLowerCase());
}

/**
 * Reduces a free-text survey answer to plain text.
 *
 * Strategy: run DOMPurify with *no* tags allowed, so every element and
 * attribute is dropped (`<img onerror=...>`, `<script>`, `<svg onload=...>`
 * cannot survive) and obfuscated payloads a hand-rolled regex would miss are
 * still neutralized.
 *
 * `KEEP_CONTENT` is left at its default (`true`) so ordinary text is preserved:
 * an answer such as `5 < 10 & 3 > 2` keeps its characters instead of being
 * silently truncated at the stray `<`. Script-bearing elements are handled by
 * `FORBID_CONTENTS`, which discards the *inner text* of `<script>`/`<style>`
 * specifically — so pasting `<script>alert(1)</script>` does not leave a bare
 * `alert(1)` behind, while `<b>Bob</b>` still yields `Bob`.
 *
 * DOMPurify HTML-encodes the surviving text, so `5 < 10` comes back as
 * `5 &lt; 10`. Since the stored value should be plain text (not markup), those
 * entities are decoded once at the end. Decoding is safe here because no tags
 * remain at that point — the value is inert text, and the render path escapes
 * it again before it reaches any HTML sink.
 *
 * On the server (no `window`), fall back to a conservative regex strip so this
 * is still safe if ever called outside the browser.
 */
export function sanitizeFreeTextInput(value: string): string {
    if (!value) {
        return '';
    }

    if (typeof window === 'undefined') {
        return stripTagsFallback(value);
    }

    const DOMPurify = createDOMPurify(window);
    const stripped = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        FORBID_CONTENTS: ['script', 'style'],
    });

    return decodeBasicEntities(stripped);
}

function stripTagsFallback(value: string): string {
    return value
        .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
        .replace(/<[^>]*>/g, '');
}

function decodeBasicEntities(value: string): string {
    return value
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&');
}
